const GTM_SERVER_ORIGIN = 'https://load.sst.eataly.it';
const GTM_CONTAINER_ID_PATTERN = /^GTM-[A-Z0-9]+$/;

type GoogleTagManagerPreferences = Pick<
    SitePreferencesCustomAttributes,
    'gtmEnabled' | 'gtmContainerId' | 'gtmServerScriptUrl'
>;

export type GoogleTagManagerConfig = {
    containerId: string;
    scriptUrl: string;
    noscriptUrl: string;
};

export function buildGoogleTagManagerBootstrap(scriptUrl: string): string {
    const serializedScriptUrl = JSON.stringify(scriptUrl)
        .replace(/</g, '\\u003c')
        .replace(/>/g, '\\u003e')
        .replace(/&/g, '\\u0026')
        .replace(/\u2028/g, '\\u2028')
        .replace(/\u2029/g, '\\u2029');

    return `(function(w,d,s,l,u){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s);j.async=true;j.src=u;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer',${serializedScriptUrl});`;
}

let loadedGoogleTagManagerScriptUrl: string | null = null;

/**
 * Injects the GTM container once. Pushes `gtm.js` synchronously so it can sit
 * after `log_state` and before the first `page_view`.
 */
export function loadGoogleTagManager(scriptUrl: string): void {
    if (typeof window === 'undefined' || loadedGoogleTagManagerScriptUrl) {
        return;
    }

    loadedGoogleTagManagerScriptUrl = scriptUrl;
    const dataLayer = (window.dataLayer ??= []);
    dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

    const script = document.createElement('script');
    script.async = true;
    script.src = scriptUrl;
    script.setAttribute('data-gtm-bootstrap', '');
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
    } else {
        document.head.appendChild(script);
    }
}

export function resetGoogleTagManagerForTests(): void {
    loadedGoogleTagManagerScriptUrl = null;
    document.querySelectorAll('script[data-gtm-bootstrap]').forEach((script) => script.remove());
}

export function resolveGoogleTagManagerConfig(preferences: GoogleTagManagerPreferences): GoogleTagManagerConfig | null {
    if (preferences.gtmEnabled !== true) {
        return null;
    }

    const containerId = preferences.gtmContainerId?.trim();
    if (!containerId || !GTM_CONTAINER_ID_PATTERN.test(containerId)) {
        return null;
    }

    let scriptUrl: URL;
    try {
        scriptUrl = new URL(preferences.gtmServerScriptUrl);
    } catch {
        return null;
    }

    if (
        scriptUrl.origin !== GTM_SERVER_ORIGIN ||
        scriptUrl.username ||
        scriptUrl.password ||
        scriptUrl.hash ||
        !scriptUrl.pathname.endsWith('.js')
    ) {
        return null;
    }

    const noscriptUrl = new URL('/ns.html', GTM_SERVER_ORIGIN);
    noscriptUrl.searchParams.set('id', containerId);

    return {
        containerId,
        scriptUrl: scriptUrl.toString(),
        noscriptUrl: noscriptUrl.toString(),
    };
}
